import React, { Suspense, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'

import MobileLayout from '../../components/MobileLayout'
import Header from '../../components/atoms/Header'
import Text from '../../components/atoms/Text'
import SlideUpPanel from '../../components/atoms/SlideUpPanel'
import KidneyScoreSheet from '../../components/organisms/KidneyScoreSheet'

import ArrowIcon from '../../components/atoms/Arrow'
import OfferingCard from '../../components/organisms/OfferingCard'
import Loader from '../../components/atoms/Loader'
import LowRiskImage from '../../assets/images/low_risk_score.webp'
import ModerateRiskImg from '../../assets/images/modarate_risk_socre.webp'
import HighRiskImg from '../../assets/images/slight_high-risk.webp'
import YellowFlaskImg from '../../assets/images/yellow_flask.webp'
import Women4Img from '../../assets/images/women4.webp'
import CancelIcon from '../../assets/icons/cancel_icon.svg'
import Button from '../../components/atoms/GoodFlipButton'
import { getCarePlans } from '../../api'
import { sendMessageToParent } from '../../utils/helpers/common'
import { trackEventCKD } from '../../analytics'
import { EVENTS } from '../../analytics/events'

const ScorePage = () => {
  const navigate = useNavigate()
  const kidneyScoreRef = useRef(null)
  const { questions = [] } = useSelector((state) => state.questionnaire)
  const { userDetails, isAuthorized } = useSelector((state) => state.auth)
  const [loading, setLoading] = useState(true)
  const [profileScore, setProfileScore] = useState(0)

  const [carePlans, setCarePlans] = useState([])

  const fetchCareplans = async () => {
    try {
      const params = {
        disease: 'Chronic Kidney Disease',
        source_type: 'GoodFlip',
        therapy_area: 'Metabolism',
      }
      const res = await getCarePlans(params)
      setCarePlans(res?.plans)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    // fetchCareplans()
  }, [])

  useEffect(() => {
    const targetQuestions = questions.filter((q) =>
      ['age', 'gender', 'comorbidities'].includes(q?.id),
    )
    let finalScore = 0
    for (const q of targetQuestions) {
      if (!q?.id || !q?.answer) continue

      if (q.id === 'age') {
        const age = q.answer.value
        if (age >= 50) {
          finalScore += age < 60 ? 2 : age < 70 ? 3 : 4
        }
      } else if (q.id === 'gender' && q.answer.value === 'Female') {
        finalScore += 1
      } else if (['comorbidities'].includes(q.id) && Array.isArray(q.answer)) {
        const conditions = new Set(q.answer)
        const riskFactors = [
          'Anemia',
          'Hypertension',
          'Proteinuria',
          'Heart attack or Stroke',
          'Congestive heart failure or Heart failure',
        ]

        for (const condition of riskFactors) {
          if (conditions.has(condition)) {
            finalScore += 1
          }
        }
      }
    }
    setProfileScore(parseInt(100 - [(finalScore / 11) * 100]))

    setTimeout(() => {
      const extraEventData = {
        country_code: userDetails?.data?.country_code || '+91',
        contact_no: userDetails?.data?.contact_no || '',
        kidney_score: parseInt(100 - [(finalScore / 11) * 100]) || 0,
      }
      console.log('trackEventCKD --> CKD_LKF_MobileNumberSubmit_StartSCORE')
      trackEventCKD(
        EVENTS.CKD_LKF_MobileNumberSubmit_StartSCORE,
        extraEventData,
      )
      setLoading(false)
    }, 200)
  }, [questions])

  const renderImage = (riskScore) => {
    if (riskScore >= 81 && riskScore <= 100) {
      return LowRiskImage
    } else if (riskScore >= 51 && riskScore <= 80) {
      return ModerateRiskImg
    } else if (riskScore >= 0 && riskScore <= 50) {
      return HighRiskImg
    }
  }

  const getTextColor = (riskScore) => {
    if (riskScore >= 81 && riskScore <= 100) {
      return '!text-SUCCESS-600'
    } else if (riskScore >= 51 && riskScore <= 80) {
      return '!text-GOLD-900'
    } else if (riskScore >= 0 && riskScore <= 50) {
      return '!text-WARNING-700'
    }
    return ''
  }

  const getCardClasses = (riskScore) => {
    if (riskScore >= 81 && riskScore <= 100) {
      return {
        bg: 'bg-BRAND-100',
        labelBg: 'bg-BRAND-700',
        labelText: 'Low Risk',
        textColor: 'text-BRAND-900',
        message:
          'Your kidney health looks good, with no significant risk of CKD. Maintain a healthy lifestyle to stay on track.',
      }
    } else if (riskScore >= 51 && riskScore <= 80) {
      return {
        bg: 'bg-[#FFFBEE]',
        labelBg: 'bg-foreground-accent-gold-primary',
        labelText: 'Moderate Risk',
        textColor: 'text-GOLD-900',
        message:
          'You are at risk of CKD. We recommend you to consult a specialist for early intervention',
      }
    } else if (riskScore >= 0 && riskScore <= 50) {
      return {
        bg: 'bg-[#FEF3F2]',
        labelBg: 'bg-[#DC6803]',
        labelText: 'High Risk',
        textColor: 'text-[#DC6803]',
        message:
          'Your kidney health is at high risk, with a significant likelihood of CKD. Seek medical advice immediately.',
      }
    }
    return {
      bg: 'bg-BRAND-100',
      labelBg: 'bg-BRAND-700',
      labelText: '',
    }
  }

  const onClickExplore = () => {
    const data = {
      ...(isAuthorized && {
        mobileNumber: userDetails?.data?.contact_no,
        name: userDetails?.data?.name,
        patientId: userDetails?.data?.patient_id,
      }),
      clientSource: 'goodflip',
      moduleName: 'care-plan-listing',
      therapyArea: 'metabolism',
      conditionName: 'kidney',
      environment:
        process.env.REACT_APP_ENV === 'development' ? 'dev' : 'production',
    }

    if (isAuthorized) {
      window.open(
        `${process.env.REACT_APP_CAREPLAN_BASE_URL}/metabolism/kidney/care-plan-listing?data=${JSON.stringify(data)}`,
        '_blank',
        'noopener',
      )
    } else {
      window.open(
        `${process.env.REACT_APP_CAREPLAN_BASE_URL}/metabolism/kidney/care-plan-listing`,
        '_blank',
        'noopener',
      )
    }
  }

  return (
    <>
      <MobileLayout>
        {loading ? (
          <Suspense fallback={<div>Loading questions...</div>}>
            <Loader
              title={
                'Loading... Why did the doctor carry a red pen? In case they needed to draw blood.'
              }
            />
          </Suspense>
        ) : (
          <>
            <Header
              onPressLeftIcon={() => {
                sendMessageToParent('NAVIGATE_BACK')
                navigate('/', { replace: true })
              }}
              showProgressBar={false}
            />
            <div className="p-4 lg:max-w-5xl lg:mx-auto">
              <div>
                <Text
                  tag={'div'}
                  variant="title"
                  size="large"
                  className={'w-full flex items-center justify-center'}
                >
                  Your Kidney Health Score
                </Text>
                <div className="relative mt-6 flex items-center justify-center lg:max-w-sm lg:mx-auto">
                  <img
                    src={renderImage(profileScore)}
                    className="w-full h-auto"
                  />
                  <Text
                    variant="display"
                    size="large"
                    tag={'div'}
                    className={classNames(
                      'absolute',
                      getTextColor(profileScore),
                    )}
                  >
                    {profileScore}
                  </Text>
                  <div className="absolute bottom-3 w-full">
                    <Text
                      variant="label"
                      size="medium"
                      className={'text-text-quartenary absolute left-[26%]'}
                    >
                      0
                    </Text>
                    <Text
                      variant="label"
                      size="medium"
                      className={'text-text-quartenary absolute left-[70%]'}
                    >
                      100
                    </Text>
                  </div>
                </div>
              </div>

              {/* risk card */}
              <div
                className={classNames(
                  'relative border px-3 pt-6 pb-4 mt-10  flex items-center justify-center rounded-2xl lg:max-w-2xl lg:mx-auto',
                  getTextColor(profileScore),
                  getCardClasses(profileScore).bg,
                )}
              >
                <div
                  className={classNames(
                    'py-1 px-4 rounded-3xl absolute top-0 -translate-y-[50%]',
                    getCardClasses(profileScore).labelBg,
                  )}
                >
                  <Text
                    variant={'title'}
                    size="large"
                    className={'text-white w-fit '}
                    tag="div"
                  >
                    {getCardClasses(profileScore).labelText}
                  </Text>
                </div>
                <Text
                  variant="body"
                  size="medium"
                  tag="p"
                  className={classNames(
                    getCardClasses(profileScore).textColor,
                    '!text-center',
                  )}
                >
                  {getCardClasses(profileScore).message}
                </Text>
              </div>

              <div className="flex flex-col items-center justify-center gap-1 mt-5">
                <Text
                  variant="body"
                  tag="div"
                  size="large"
                  className={'text-text-primary mt-4'}
                >
                  Want to know more about this questionnaire?
                </Text>
                <div
                  className="flex items-center gap-2"
                  onClick={() => {
                    kidneyScoreRef?.current?.show()
                  }}
                >
                  <Text
                    variant="label"
                    size="large"
                    className={
                      'text-background-brand-teal underline !font-bold'
                    }
                  >
                    See How It Works
                  </Text>
                  <ArrowIcon color="#199EA5" direction="right" />
                </div>
              </div>
              {profileScore >= 81 && profileScore <= 100 ? (
                // <div>
                //   <div className="mt-12">
                //     <div className="flex flex-col gap-2">
                //       <Text
                //         variant="headline"
                //         size="large"
                //         className={'text-text-primary'}
                //         tag={'p'}
                //       >
                //         Your Next Step to Better Kidney Care
                //       </Text>
                //       <Text
                //         variant="body"
                //         size="medium"
                //         className={'text-text-quartenary'}
                //         tag={'p'}
                //       >
                //         Based on your Kidney Profile Score, we've designed a
                //         personalized care program to support and improve your
                //         kidney health. Take action today for a healthier
                //         tomorrow
                //       </Text>
                //       <Text
                //         variant="label"
                //         size="small"
                //         className={
                //           'w-fit rounded-2xl px-1.5 py-1 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-300'
                //         }
                //         tag="div"
                //       >
                //         Recommended For You
                //       </Text>
                //     </div>
                //   </div>
                //   <div className="mt-6">
                //     {(carePlans || [])?.map((care) => (
                //       <OfferingCard
                //         careplan={care}
                //         onClickExplore={onClickExplore}
                //       />
                //     ))}
                //   </div>
                // </div>
                <></>
              ) : (
                <div className="mt-12 w-full lg:max-w-4xl lg:mx-auto">
                  <div className="flex flex-col gap-2">
                    <Text
                      variant="label"
                      size="small"
                      className={
                        'w-fit rounded-2xl px-1.5 py-1 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-300'
                      }
                      tag="div"
                    >
                      Your Next Step
                    </Text>
                    <Text
                      variant="headline"
                      size="large"
                      className={'text-text-primary'}
                      tag={'p'}
                    >
                      Act Now to Safeguard Your Kidney Health
                    </Text>
                    <Text
                      variant="body"
                      size="medium"
                      className={'text-text-quartenary'}
                      tag={'p'}
                    >
                      Your Kidney Profile Score indicates a moderate to high
                      risk of CKD. Diagnostics are essential to create a care
                      plan tailored to your needs. Don't delay—take the next
                      step today!
                    </Text>
                  </div>
                  <div className="flex items-center w-full px-4 border-b-2 border-[#E4E7EC] mt-6 lg:max-w-sm lg:mx-auto">
                    <img
                      src={YellowFlaskImg}
                      className="w-1/2 relative left-5"
                    />
                    <img src={Women4Img} className="w-1/2 relative right-5" />
                  </div>
                  <div className="mt-6 mb-4">
                    <Button
                      title={'Book Diagnostics Now'}
                      onClick={() => {
                        navigate('/book-diagnostic?fromButtonPress=true')
                      }}
                      className="!m-0 !mt-0"
                      type="goodflip_primary"
                      rightIcon={null}
                      leftIcon={null}
                    ></Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </MobileLayout>
      <SlideUpPanel
        ref={kidneyScoreRef}
        header={
          <div className="sticky flex justify-end items-center p-3">
            <button
              onClick={() => {
                kidneyScoreRef?.current?.hide()
              }}
              className="p-1.5 rounded-full bg-white border-2 border-[#F2F4F7] "
            >
              <img src={CancelIcon} />
            </button>
          </div>
        }
      >
        <KidneyScoreSheet />
      </SlideUpPanel>
    </>
  )
}

export default ScorePage
